/* eslint-disable react/prop-types */
/**
 * DiscountCodes — admin surface for minting creator codes and reading their
 * attribution, at /discount-codes.
 *
 * The question this page exists to answer is "which bookings came from who".
 * Everything on it is arranged around that: each code is a row, and each row
 * carries its creator, its funnel (applied → booked), the dollars it gave away,
 * and the trip value it brought in. Expanding a row lists the actual bookings.
 *
 * Two numbers deserve care when reading them:
 *
 *   Conversion counts every redemption that ever went live, including the ones
 *   the customer later removed. Hiding those would flatter the rate and lose
 *   the more interesting signal — people who wanted the code and still walked.
 *
 *   Pending is discount promised on carts still in flight. It is not a cost
 *   yet, and it is not revenue either; it is exposure, so it renders apart from
 *   the given figure rather than summed into it.
 *
 * Security posture: `discount_codes` runs RLS-on-with-no-policies, so this page
 * cannot read the table directly no matter who is signed in — every call goes
 * through requireAdmin()-gated endpoints on the service role. The client-side
 * role check below only decides what to render.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Tag, Plus, Loader2, RefreshCw, Link2, Check, ChevronDown, ChevronRight,
  ShieldAlert, TrendingUp, X,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { User as UserEntity } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { sanitizeDiscountCode } from '@/utils/discountCodes';
import { publicOrigin } from '@/lib/publicOrigin';

// Mirrors Layout.jsx / InquiriesDesk.jsx. Backed by JWT app_metadata.roles,
// which users cannot self-edit (unlike user_metadata).
function isCurrentUserAdmin(u) {
  if (!u) return false;
  const roles = u?.app_metadata?.roles || u?.appMetadata?.roles || [];
  return (Array.isArray(roles) && roles.includes('admin')) || u?.role === 'admin';
}

const money = (cents) =>
  `$${((Number(cents) || 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const PLATFORMS = ['tiktok', 'instagram', 'youtube', 'x', 'podcast', 'other'];

const CARD = 'bg-white/[0.03] border border-white/10 rounded-2xl';

function Stat({ label, value, tone = 'default' }) {
  const toneClass =
    tone === 'good' ? 'text-emerald-300'
      : tone === 'warn' ? 'text-amber-300'
        : 'text-white/90';
  return (
    <div>
      <div className="text-[11px] text-white/45">{label}</div>
      <div className={`text-sm tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

function CreateCodeForm({ onCreated, buildAuthHeader }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', partner_name: '', partner_handle: '', partner_platform: 'tiktok',
    discount_dollars: '20', max_redemptions: '', expires_at: '',
    min_trip_dollars: '100', per_customer_limit: '1', notes: '',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const code = sanitizeDiscountCode(form.code);
    if (!code) {
      toast.error('Codes are 3–24 characters: letters, numbers, dashes or underscores.');
      return;
    }
    const dollars = Number(form.discount_dollars);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error('Enter a discount amount greater than $0.');
      return;
    }

    setSaving(true);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/discount-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({
          code,
          partner_name: form.partner_name || null,
          partner_handle: form.partner_handle || null,
          partner_platform: form.partner_platform,
          discount_cents: Math.round(dollars * 100),
          max_redemptions: form.max_redemptions === '' ? null : Number(form.max_redemptions),
          per_customer_limit: Number(form.per_customer_limit) || 1,
          min_trip_total_cents: Math.round((Number(form.min_trip_dollars) || 0) * 100),
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
          notes: form.notes || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      toast.success(`${code} created.`);
      setForm((f) => ({ ...f, code: '', partner_name: '', partner_handle: '', notes: '' }));
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.message || 'Could not create code.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} className={`${BRAND_PRIMARY_BTN_CLASS} h-10`}>
        <Plus className="w-4 h-4 mr-2" /> New code
      </Button>
    );
  }

  const field = 'bg-white/5 border-white/10 text-white placeholder:text-white/35 rounded-xl h-10';
  const label = 'block text-[11px] text-white/45 mb-1.5';

  return (
    <div className={`${CARD} p-5 w-full`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-lg font-semibold">New discount code</h3>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white/70">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className={label}>Code</label>
          <Input
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="JAY20"
            className={`${field} font-medium tracking-wide`}
          />
        </div>
        <div>
          <label className={label}>Discount ($ off trip total)</label>
          <Input type="number" min="1" step="1" value={form.discount_dollars}
            onChange={set('discount_dollars')} className={field} />
        </div>
        <div>
          <label className={label}>Creator name</label>
          <Input value={form.partner_name} onChange={set('partner_name')}
            placeholder="Jane Doe" className={field} />
        </div>
        <div>
          <label className={label}>Handle</label>
          <Input value={form.partner_handle} onChange={set('partner_handle')}
            placeholder="@janedoe" className={field} />
        </div>
        <div>
          <label className={label}>Platform</label>
          <select
            value={form.partner_platform}
            onChange={set('partner_platform')}
            className={`${field} w-full px-3 text-sm outline-none`}
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p} className="bg-neutral-900">{p}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>Minimum trip total ($)</label>
          <Input type="number" min="0" step="10" value={form.min_trip_dollars}
            onChange={set('min_trip_dollars')} className={field} />
        </div>
        <div>
          <label className={label}>Max redemptions (blank = unlimited)</label>
          <Input type="number" min="1" value={form.max_redemptions}
            onChange={set('max_redemptions')} placeholder="Unlimited" className={field} />
        </div>
        <div>
          <label className={label}>Uses per customer</label>
          <Input type="number" min="1" value={form.per_customer_limit}
            onChange={set('per_customer_limit')} className={field} />
        </div>
        <div>
          <label className={label}>Expires (blank = never)</label>
          <Input type="date" value={form.expires_at} onChange={set('expires_at')} className={field} />
        </div>
      </div>

      <div className="mt-4">
        <label className={label}>Notes</label>
        <Input value={form.notes} onChange={set('notes')}
          placeholder="Campaign, rate, anything worth remembering later"
          className={field} />
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="ghost" onClick={() => setOpen(false)}
          className="text-white/60 hover:text-white rounded-xl h-10">Cancel</Button>
        <Button onClick={submit} disabled={saving} className={`${BRAND_PRIMARY_BTN_CLASS} h-10`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create code'}
        </Button>
      </div>
    </div>
  );
}

function CodeRow({ row, onToggleActive, busy }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const s = row.stats || {};

  const referralLink = useMemo(
    () => `${publicOrigin()}/?ref=${encodeURIComponent(row.code)}`,
    [row.code]
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — select the link manually.');
    }
  };

  const expired = row.expires_at && new Date(row.expires_at) <= new Date();
  const exhausted = row.max_redemptions != null
    && (s.converted + s.applied) >= row.max_redemptions;

  return (
    <div className={CARD}>
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-white text-lg font-semibold tracking-wide">{row.code}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-white/60">
                {money(row.discount_cents)} off
              </span>
              {!row.active && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-white/50">
                  Disabled
                </span>
              )}
              {row.active && expired && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-300">
                  Expired
                </span>
              )}
              {row.active && !expired && exhausted && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-300">
                  Limit reached
                </span>
              )}
            </div>
            <div className="text-sm text-white/55 mt-1">
              {row.partner_name || 'Unassigned'}
              {row.partner_handle ? ` · ${row.partner_handle}` : ''}
              {row.partner_platform ? ` · ${row.partner_platform}` : ''}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={copyLink}
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/10 hover:text-white rounded-xl h-9"
            >
              {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
                : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <Button
              variant="outline" size="sm" disabled={busy}
              onClick={() => onToggleActive(row)}
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/10 hover:text-white rounded-xl h-9"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (row.active ? 'Disable' : 'Enable')}
            </Button>
          </div>
        </div>

        {/* The funnel, left to right in the order it happens. `Reach` is people,
            not page views — someone who opened the link three times is one
            person, and counting the views would flatter every campaign. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mt-5">
          <Stat label="Reach" value={s.reach ?? 0} tone={s.reach > 0 ? 'good' : 'default'} />
          <Stat label="In cart" value={s.applied ?? 0} />
          <Stat label="Bookings" value={s.converted ?? 0} tone={s.converted > 0 ? 'good' : 'default'} />
          <Stat label="Removed" value={s.released ?? 0} />
          <Stat
            label="Reach → booked"
            value={s.click_to_booking_rate == null ? '—' : `${Math.round(s.click_to_booking_rate * 100)}%`}
          />
          <Stat label="Discount given" value={money(s.discount_given_cents)} />
          <Stat label="Trip value" value={money(s.trip_value_cents)} tone="good" />
        </div>

        {(s.visits > s.reach || s.via_link > 0 || s.top_referrer || s.discount_pending_cents > 0) && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[11px] text-white/40">
            {s.visits > s.reach && <span>{s.visits} total visits</span>}
            {s.carted_from_click > 0 && <span>{s.carted_from_click} of them started a trip</span>}
            {s.top_referrer && <span>mostly from {s.top_referrer}</span>}
            {s.via_link > 0 && <span>{s.via_link} applied via link</span>}
            {s.discount_pending_cents > 0 && (
              <span>{money(s.discount_pending_cents)} promised on carts still open</span>
            )}
          </div>
        )}

        {s.converted > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1.5 text-[13px] text-white/55 hover:text-white/80 mt-4"
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {expanded ? 'Hide bookings' : `Show ${s.converted} booking${s.converted === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-white/10 px-4 sm:px-5 py-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left text-[11px] text-white/40">
                  <th className="pb-2 font-normal">Reference</th>
                  <th className="pb-2 font-normal">Customer</th>
                  <th className="pb-2 font-normal">Booked</th>
                  <th className="pb-2 font-normal text-right">Trip total</th>
                  <th className="pb-2 font-normal text-right">Discount</th>
                  <th className="pb-2 font-normal text-right">Source</th>
                </tr>
              </thead>
              <tbody>
                {(s.bookings || []).map((b) => (
                  <tr key={b.booking_id} className="border-t border-white/[0.06]">
                    <td className="py-2 text-white/85">{b.booking_reference || '—'}</td>
                    <td className="py-2 text-white/55">{b.user_email || '—'}</td>
                    <td className="py-2 text-white/55">
                      {b.converted_at ? new Date(b.converted_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 text-right text-white/85 tabular-nums">
                      {b.trip_total_cents ? money(b.trip_total_cents) : '—'}
                    </td>
                    <td className="py-2 text-right text-emerald-300 tabular-nums">
                      −{money(b.discount_cents)}
                    </td>
                    <td className="py-2 text-right text-white/45">
                      {b.via_referral_link ? 'link' : 'typed'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DiscountCodes() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await UserEntity.me();
        if (!cancelled) setUser(me || null);
      } catch {
        /* signed out */
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buildAuthHeader = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const tok = data?.session?.access_token;
      return tok ? `Bearer ${tok}` : null;
    } catch {
      return null;
    }
  }, []);

  const admin = isCurrentUserAdmin(user);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/discount-codes', { headers: { Authorization: authHeader } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setCodes(data.codes || []);
    } catch (err) {
      setError(err?.message || 'Failed to load discount codes.');
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeader]);

  useEffect(() => {
    if (!authReady || !admin) return;
    load();
  }, [authReady, admin, load]);

  const toggleActive = useCallback(async (row) => {
    setBusyId(row.id);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/discount-codes/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({ active: !row.active }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setCodes((prev) => prev.map((c) => (c.id === row.id ? { ...c, active: !row.active } : c)));
      toast.success(`${row.code} ${row.active ? 'disabled' : 'enabled'}.`);
    } catch (err) {
      toast.error(err?.message || 'Could not update code.');
    } finally {
      setBusyId(null);
    }
  }, [buildAuthHeader]);

  const totals = useMemo(() => codes.reduce((acc, c) => {
    const s = c.stats || {};
    acc.reach += s.reach || 0;
    acc.bookings += s.converted || 0;
    acc.given += s.discount_given_cents || 0;
    acc.value += s.trip_value_cents || 0;
    return acc;
  }, { reach: 0, bookings: 0, given: 0, value: 0 }), [codes]);

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-white/40" />
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className={`${CARD} p-8 text-center max-w-sm`}>
          <ShieldAlert className="w-6 h-6 text-white/40 mx-auto mb-3" />
          <h1 className="text-white text-lg font-semibold mb-1">Admin only</h1>
          <p className="text-sm text-white/55">
            Discount codes are managed by the Defera team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Discount Codes · Defera</title></Helmet>
      <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <div>
            <div className="flex items-center gap-2.5">
              <Tag className="w-5 h-5 text-white/50" />
              <h1 className="text-white text-2xl font-semibold tracking-tight">Discount codes</h1>
            </div>
            <p className="text-sm text-white/50 mt-1.5">
              One code per creator. Every booking traces back to whoever sent it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={load} disabled={loading}
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/10 hover:text-white rounded-xl h-10"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {codes.length > 0 && (
          <div className={`${CARD} px-5 py-4 mt-6 flex flex-wrap gap-x-10 gap-y-3`}>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400/70" />
              <span className="text-sm text-white/55">Across all codes</span>
            </div>
            <Stat label="People reached" value={totals.reach} />
            <Stat label="Bookings attributed" value={totals.bookings} tone="good" />
            <Stat label="Trip value" value={money(totals.value)} tone="good" />
            <Stat label="Discount given" value={money(totals.given)} />
          </div>
        )}

        <div className="mt-6">
          <CreateCodeForm onCreated={load} buildAuthHeader={buildAuthHeader} />
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-400/20 bg-red-400/[0.06] px-5 py-4 text-sm text-red-200">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-3">
          {loading && codes.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-white/45 px-1 py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading codes…
            </div>
          )}

          {!loading && codes.length === 0 && !error && (
            <div className={`${CARD} px-6 py-12 text-center`}>
              <Tag className="w-6 h-6 text-white/25 mx-auto mb-3" />
              <p className="text-white/85">No codes yet</p>
              <p className="text-sm text-white/45 mt-1.5 max-w-sm mx-auto">
                Create one per creator. They share the code or their link, and the
                bookings show up here against their name.
              </p>
            </div>
          )}

          {codes.map((row) => (
            <CodeRow key={row.id} row={row} onToggleActive={toggleActive} busy={busyId === row.id} />
          ))}
        </div>
      </div>
    </>
  );
}
